import { createContext, useContext, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../lib/utils.js';
import { focusRing } from '../lib/focus.js';
import { ICON } from '../lib/iconSize.js';

export interface PaginationProps {
  /** 1-based current page. */
  page: number;
  /** Total number of pages. */
  pageCount: number;
  /** Total item count across all pages. */
  total: number;
  /** 1-based index of the first item shown. */
  from: number;
  /** 1-based index of the last item shown. */
  to: number;
  onPageChange: (page: number) => void;
  /** Singular noun for the range label, e.g. "worker" → "worker(s)". Read by
   *  the DEFAULT (English) range label only — see `PaginationLabels`. */
  itemLabel?: string;
  className?: string;
}

/* --- The words, so a tenant reading Arabic sees Arabic ---------------------
 *
 * This component hard-coded "Showing", "of", "Previous" and "Next", and
 * pluralised by appending an "s". That is three faults on a non-English
 * tenant: the chrome is untranslated, `${itemLabel}s` is not one of Arabic's
 * six plural forms, and the pager is the one control on a table that states
 * HOW MUCH of the data you are looking at. A screen that could not accept the
 * cost hand-rolled its own pager instead — `AttendancePager`, now deleted —
 * which is the second control language §6 exists to prevent.
 *
 * IT IS A CONTEXT, NOT SIX PROPS. Six label props would have put the same six
 * `t()` lookups at nineteen call sites, and a screen that got tired of copying
 * them is exactly how the private pager appeared the first time. The words
 * belong to the APP, not to the page: one provider near the root answers for
 * every pager in it, and a page that renders a pager writes nothing at all.
 *
 * `range` and `page` are FUNCTIONS taking the figures, not finished strings,
 * because the provider is mounted once and the numbers change per pager. The
 * other four are plain nodes.
 *
 * The default is today's English, spelled exactly as it was, so a `Pagination`
 * rendered with no provider above it — in a unit test, in the installer —
 * behaves as it always has and no caller breaks.
 * ------------------------------------------------------------------------ */
export interface PaginationLabels {
  /** "Showing 1–20 of 57". `itemLabel` is the caller's singular noun, which a
   *  translated label is free to ignore — the noun cannot be pluralised by a
   *  parameter in most languages, and the table's own caption already says
   *  what its rows are. */
  range: (r: {
    from: number;
    to: number;
    total: number;
    itemLabel: string;
  }) => ReactNode;
  /**
   * "Page 2 of 5" — the pager's ACCESSIBLE position, not a visible line.
   *
   * It used to render between the two controls. It said, in words, what
   * "Showing 1–10 of 49" a few centimetres to its left had already said in
   * figures: a reader on page one of five is being told twice, and the band
   * carrying both is the thing the owner called "too much". It is still
   * ANNOUNCED — it is the one statement of where you are that does not need
   * arithmetic, and dropping it outright would cost a screen-reader user the
   * fact — so it lives in a visually-hidden live region.
   */
  page: (r: { page: number; pageCount: number }) => ReactNode;
  /**
   * The two controls' accessible names. There is no visible `previous`/`next`
   * TEXT any more: the caps read as chevrons, which is what the reader of a
   * ten-row table is scanning for at the end of the band, and two words that
   * only ever mean "the arrow beside them" are two words of chrome.
   */
  previousAria: string;
  nextAria: string;
}

const DEFAULT_LABELS: PaginationLabels = {
  range: ({ from, to, total, itemLabel }) => (
    <>
      Showing{' '}
      <span className="font-medium text-foreground">
        {from}–{to}
      </span>{' '}
      of <span className="font-medium text-foreground">{total}</span>{' '}
      {total === 1 ? itemLabel : `${itemLabel}s`}
    </>
  ),
  page: ({ page, pageCount }) => `Page ${page} of ${pageCount}`,
  previousAria: 'Previous page',
  nextAria: 'Next page',
};

const PaginationLabelsContext = createContext<PaginationLabels>(DEFAULT_LABELS);

/** Mount once near the app root with the tenant's translated words. */
export function PaginationLabelsProvider({
  labels,
  children,
}: {
  labels: PaginationLabels;
  children: ReactNode;
}): JSX.Element {
  return (
    <PaginationLabelsContext.Provider value={labels}>
      {children}
    </PaginationLabelsContext.Provider>
  );
}

// Modelled on `<Button variant="outline" size="sm">` — kept as a local constant
// rather than the component itself so the pager stays free of a circular
// import and can size its two controls independently of the button scale. It
// now uses that independence for one property, and only one:
// `rounded-xs` (8px) and not the `rounded-control` (10px) the rest of the small
// controls take: the handoff's pager cap is its own shape — `min-height: 30px;
// padding: 0 9px; border-radius: 8px` (Hadir Employees.dc.html:325) — a tighter
// corner on a smaller cap. The 30px height is NOT copied; 32px is the floor the
// rest of the small controls sit at and a one-off 30px pager would be the only
// control in the app off that rail.
const btn = cn(
  // SQUARE, because the cap holds one glyph now rather than a word: `size-8`
  // keeps the 32px floor the rest of the small controls sit at in both axes,
  // so the two caps read as a pair rather than as two shrunken buttons.
  'inline-flex size-8 items-center justify-center rounded-xs border border-border text-foreground',
  'transition-colors duration-fast ease-standard hover:bg-muted',
  'disabled:pointer-events-none disabled:opacity-50',
  focusRing,
  '[@media(pointer:coarse)]:min-h-touch'
);

/**
 * Compact pager for client-paginated lists. Renders a "Showing X–Y of N"
 * range and two chevron controls. Returns null when there's a single page
 * (nothing to page), so callers can drop it in unconditionally.
 *
 * Uses logical alignment (`justify-between`, `text-start`) so it mirrors under
 * RTL, and semantic tokens so it themes with light/dark.
 */
export function Pagination({
  page,
  pageCount,
  total,
  from,
  to,
  onPageChange,
  itemLabel = 'item',
  className,
}: PaginationProps): JSX.Element | null {
  const labels = useContext(PaginationLabelsContext);
  if (pageCount <= 1) return null;
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-component border-t border-border py-cell-y',
        // `px-tile` (20px), not `px-cell-x` (16px): the pager band is the table
        // shell's own footer and the handoff insets it on the shell's rail —
        // `padding: 13px 20px` (Hadir Employees.dc.html:322) — so the range line
        // starts under the first column rather than 4px inside it.
        'px-tile',
        // The band carries its own type the way the mock's does: 12.5px in
        // `--foreground-tertiary` (#6A6659), which the two readouts below
        // inherit. `font-normal` because `text-label` is a 600 step and this is
        // a sentence, not a label — the only emphasis in the band belongs to the
        // figures, which set their own weight.
        'text-label font-normal text-foreground-tertiary',
        className
      )}
    >
      <p aria-live="polite">{labels.range({ from, to, total, itemLabel })}</p>
      <div className="flex items-center gap-tight">
        {/* ANNOUNCED, NOT DRAWN — see `PaginationLabels.page`. It sits inside
            the live region's own element so a page change is spoken once, as
            "Page 2 of 5", rather than the range and the position racing. */}
        <span className="sr-only" aria-live="polite">
          {labels.page({ page, pageCount })}
        </span>
        <button
          type="button"
          className={btn}
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label={labels.previousAria}
        >
          {/* `rtl:rotate-180` on both: the chevrons point at the DIRECTION of
              travel, which mirrors with the script. */}
          <ChevronLeft size={ICON.control} aria-hidden className="rtl:rotate-180" />
        </button>
        <button
          type="button"
          className={btn}
          onClick={() => onPageChange(page + 1)}
          disabled={page >= pageCount}
          aria-label={labels.nextAria}
        >
          <ChevronRight size={ICON.control} aria-hidden className="rtl:rotate-180" />
        </button>
      </div>
    </div>
  );
}
